// Player-facing changelog. Newest first; keep ~20 entries and prune the tail.
// Maintained with every push to main — the client shows the top entries in the
// What's-New modal after each forced update. Shared so both bundles agree.

export interface ChangelogEntry {
  date: string // YYYY-MM-DD
  title: string
  points: string[]
}

export const CHANGELOG: ChangelogEntry[] = [
  {
    date: '2026-09-23',
    title: 'Defend your supplies',
    points: [
      'Independent villages can raid your Town Hall stores and storehouses with real equipped villagers. A one-minute warning gives you time to gather defenders; new villages keep their three-game-day protection.',
      'Raiders must reach the storage entrance and stop for 20 seconds to load. Each can steal up to 15 supplies, limited by carrying strength, with a maximum of 60 per raid. They must carry the goods home before their village receives them.',
      'Loaded raiders show red cargo badges with their stolen materials. Diplomacy keeps the threat visible during their retreat and can locate raiders currently in sight.',
      'Defeated raiders drop their stolen supplies. Your haulers recover nearby piles; for loot outside your hauling border, send an unassigned, unarmed worker onto the pile to carry it home. Ordinary logging and hauling priorities stay unchanged.',
    ],
  },
  {
    date: '2026-09-23',
    title: 'Closer camp plans and clearer tool needs',
    points: [
      'The opening guide now favors nearby, reachable farm and house plots instead of sending you to distant tree-free clearings. Trees beside camp can be cleared by your crew; cliffs, water and long detours are avoided.',
      'Workers without the right tool now show an amber tool icon and exclamation mark above their heads. Hover to see what they need; the warning follows the current work phase and clears when they equip the tool.',
    ],
  },
  {
    date: '2026-09-21',
    title: 'Consistent world lighting',
    points: [
      'Fixed a dark, striped square that followed the camera when looking across the map. Terrain, water and scenery now receive shadows on the correct side of their surfaces.',
    ],
  },
  {
    date: '2026-09-21',
    title: 'Fields with a little more life',
    points: [
      'Farms now have hex-shaped tilled beds, denser wheat, a thatched tool shelter, a scarecrow and a harvest basket. The three fields show green shoots, growing crops and golden wheat, with gentle wind movement.',
      'Working crews drive the visual crop cycle, while the basket reflects food actually waiting for collection. Paused, empty and full farms stop their work cycle; food rates and costs stay the same.',
      'Select a farm for an illustrated overview of its food output, soil, farmers, tools, skills and harvest waiting for pickup. The card also explains when work is paused or waiting for a crew or hauler.',
      'Workers keep their bodies and shoulders connected while working, with smoother tool strokes and natural transitions back to walking and carrying.',
    ],
  },
  {
    date: '2026-09-21',
    title: 'Independent villages grow and compete',
    points: [
      'New players get an independent village about 30 hexes away, unless one already exists within 40 hexes. These neighbors begin with the same campsite, two founders and starting supplies, and keep playing while you are offline.',
      'Independent villages build farms, craft their own tools, replant forests and upgrade toward a stone keep. They manage real supplies and can grow to 30 people, reserving workers to keep their village running.',
      'Established neighbors scout the map and arm small raiding parties with weapons they actually craft. They warn you 60 seconds before a raid, target exposed outposts, and retreat when the fight turns against them. New players are protected from their raids for three game days.',
      'Open Diplomacy to see each independent village’s growth stage, current activity and attitude toward you. Raid warnings survive reconnecting, and retreating villages wait three game days before another raid.',
      'Repair crews now stay on damaged town halls and houses until repairs are finished instead of repeatedly dropping the job.',
    ],
  },
  {
    date: '2026-09-20',
    title: 'Sign in with Discord',
    points: [
      'Discord now sits alongside Google and Steam on the sign-in card. One click and you are in — your Discord display name comes across as your account name, while your village keeps its own anonymous realm name as always.',
      'A heads-up worth reading: an account is tied to the button you signed in with. If you have been playing with Google and you sign in with Discord instead, you will get a brand-new village rather than your existing one — so come back through the same door you first used.',
    ],
  },
  {
    date: '2026-09-20',
    title: 'Serf Land has a Discord',
    points: [
      'There is now a community Discord: chat in #general, show off your realm in #screenshots and #village-showoff, swap tactics in #strategy, and report bugs in the #bug-reports forum so they can be tagged and tracked.',
      'A daily snapshot of the living world lands in #world-digest — realms, villagers, buildings and who is awake — so you can see the world moving even when you are not playing.',
      'Patch notes post themselves to #patch-notes. These very notes are the first.',
    ],
  },
  {
    date: '2026-09-13',
    title: 'Build your camp and welcome new villagers',
    points: [
      'Choose the 500-wood Settler Camp project in the Village Council. Your village gathers toward the goal and shows when more storage is needed. Upgrade to unlock a village watch with real volunteers and crafted spears.',
      'New villagers arrive every 45 seconds while food is above zero and housing is available, regardless of production rate or spare tools. Pause arrivals in the Council whenever you want.',
      'The Council explains food production and consumption. Fishing now produces 3 food per equipped worker each minute, and wandering villagers mark useful discoveries on the map.',
      'Wildlife leaves new villages alone for two game days. The Council shows the remaining protection and warns before it ends.',
    ],
  },
  {
    date: '2026-09-13',
    title: 'Clearer crafting and reliable deliveries',
    points: [
      'Building panels have separate Info, Crafting, Storage and Workers tabs. The tutorial guides you through these permanent controls while the rest of the game stays accessible.',
      'Haulers leave construction materials at unfinished buildings. If storage cannot accept their cargo, they wait and resume when space opens instead of walking endlessly.',
      'Hover storage to see its contents and space. Warning markers turn yellow at 80% full and red at 95%; hauling badges show the resource being carried.',
    ],
  },
  {
    date: '2026-09-12',
    title: 'Wander and uncover the world',
    points: [
      'Exploring villagers now roam through the world and reveal the map as they walk. Send a free villager from Explore the map, or choose someone directly in their panel.',
      'Follow an explorer or call them home whenever you need them. Compass icons and gold minimap rings make them easy to find.',
      'The opening finishes with a hammer, farm and house. Exploration is optional, with no paid trips or preset reward destinations.',
    ],
  },
  {
    date: '2026-09-12',
    title: 'Keep playing through your first craft',
    points: [
      'The tutorial keeps your build menu, resources, people and other controls visible. A compact guide highlights the next click without darkening the game or pulling you back to a panel.',
      'Queue your first hammer, then plan a farm and a house while it is made. Live crafting status shows progress, fetching materials or a paused bench, and plans count toward your next tutorial steps.',
      'One founder prioritizes the first hammer while the other can deliver supplies, move or scout. Construction still needs the finished tool.',
    ],
  },
  {
    date: '2026-09-12',
    title: 'From campsite to stone keep',
    points: [
      'Your Town Hall begins as a tiny campsite with two founders and room for five people, then grows through five architectural stages on the same seven-hex plot.',
      'Select your hall to upgrade with stored resources and a growing village. Each stage adds housing, storage and durability; the first needs two people and 500 wood.',
      'Crafting continues during upgrades. Progress survives reconnects and saved-world reloads; pausing the hall pauses its upgrade too.',
    ],
  },
  {
    date: '2026-09-11',
    title: 'Two founders and a first hammer',
    points: [
      'New villages begin with two empty-handed people. Open the Town Hall crafting bench and make your first hammer for 10 wood; the guide highlights exactly what to click.',
      'Automatic tool orders wait for your first craft. Build a house to welcome more people, and let your small crew share construction, deliveries and farming.',
      'Existing villages keep their people and equipment. New starts no longer receive a free hammer when a saved world reloads.',
    ],
  },
  {
    date: '2026-09-11',
    title: 'Give each other a little room',
    points: [
      'People anticipate oncoming walkers and use consistent passing lanes. Crowds spread out without rotating whenever they change hexes.',
      'Walkers take gradual turns around walls and tree stands. Routes go around completed buildings, preserve access to workplaces, and detour around full hexes.',
      'Movement prediction waits at crowded hexes instead of running ahead into a queue and snapping back.',
    ],
  },
  {
    date: '2026-09-11',
    title: 'A clearer first village',
    points: [
      'Start with three clear steps: place a farm, add a house, then send a scout for your first discovery. Each step has a direct action and highlights where to click.',
      'The camera starts closer, and advanced menus stay tucked away during the opening. Your people build and work automatically while you explore.',
      'Choose a lasting relic bonus or extra supplies to finish the opening. Skip at any time to restore every menu, or replay from Help.',
    ],
  },
  {
    date: '2026-09-10',
    title: 'Follow a trail. Bring back a story.',
    points: [
      'Explore the wilds directly from the map. Nearby gardens, ruined arches and bandit caches have visible landmarks and one-click scouting.',
      'Successful scouts return with supplies and a discovery. Keep its relic for a permanent village bonus, or trade the find for supplies and renown.',
      'Restore three relics across nine levels. Successful expeditions now have a 30-second rest; free scavengers can join without individual worker orders.',
    ],
  },
  {
    date: '2026-09-10',
    title: 'Run a village without assigning every job',
    points: [
      'Place buildings and choose priorities: your villagers now staff workplaces, craft missing work tools and gather nearby wood automatically. Helpers stay free to keep construction and deliveries moving.',
      'The Village Council (J) offers balanced, food and industry priorities. Food shortages take priority; manual crews, paused buildings and expedition parties stay under your control.',
      'Milestone rewards arrive automatically. Worker panels put automatic staffing first, and the opening tutorial teaches placing a farm instead of crafting and equipping individual tools.',
      'Existing saved crews are preserved as manual. Switch a building to automatic in its Workers panel, or turn automatic management off in the Council for full manual control.',
    ],
  },
  {
    date: '2026-09-10',
    title: 'A working tutorial close button',
    points: ['The tutorial’s X and Done buttons now receive clicks instead of passing them through to the map. Their larger touch target also makes dismissing the tour easier on phones.'],
  },
  {
    date: '2026-09-10',
    title: 'Give your village a story',
    points: [
      'Open the Founder’s Journal with J: eleven milestones reward building, crafting, farming, trading and exploration with supplies and renown. Grow from a hamlet into a free city.',
      'Spend renown on crop rotation, master builders, better workbenches and a merchant charter. Each has three permanent levels; faster construction and crafting keep the same material costs.',
      'Caravans bring three contracts every ten minutes. Trade surplus supplies for scarce goods, or buy emergency provisions. Larger villages can hold a feast for a temporary harvest boost.',
      'Send your people on real map expeditions: forage a hidden meadow, salvage a forgotten quarry, or bring an armed party to a bandit cache. Follow the marker, defeat any guards and bring survivors home. You can recall a party at any time.',
      'The opening tutorial now teaches crafting an axe, since your starting villagers already have a hammer and hoe. Production breakdowns include the new bonuses, and a full armory no longer consumes materials for an already-finished tool.',
    ],
  },
  {
    date: '2026-09-10',
    title: 'A fresh look for your village',
    points: [
      'A cleaner village interface: labeled resources, clearer panels, and a build menu with portraits of the actual buildings. Phone layouts keep building names visible and give selection panels room above the dock.',
      'More detailed low-poly cottages, town halls, stores, watchtowers and smithies, plus planted wheat, rocky mines, and new faceted villagers and wildlife. Buildings keep their working storage, armories and workshops.',
      'Explore and rotate the models in the new art gallery at /?gallery — no sign-in needed.',
    ],
  },
  {
    date: '2026-09-08',
    title: 'Steadier under a crowd',
    points: [
      'The server now clears out connections that arrive and never sign in, and keeps a lid on how many can wait at the door at once — so a rush of them can no longer crowd out players who are actually trying to join. Nothing changes in play; your session, and the “playing elsewhere” handover between tabs, work exactly as before.',
    ],
  },
  {
    date: '2026-07-19',
    title: 'No more hoe-less farmhands',
    points: [
      'Fixed farms (and every trade) auto-staffing serfs without the right tool: when a building finishes it now only drafts a serf who is JOBLESS and already holds the proper tool — or, if the armory has one spare, a free serf who goes and collects it first. Nobody else gets pulled in; staffing beyond that is yours via + assign.',
      'Crew members holding the WRONG tool now fix themselves: a hammer-holder pinned to a farm walks to the armory and swaps for the hoe the moment one is spare. (Armed serfs keep their weapons — only the training dummy wants those.)',
      'And every serf now shows their workplace: select one and a chip names the building they work — click it to fly there, hover it to light the building up in the world.',
    ],
  },
  {
    date: '2026-07-17',
    title: 'Building sites show their full footprint',
    points: [
      'A building that covers more than one hex — a farm, the town hall — now lights each of its tiles with an amber footprint while it’s under construction, so you can see the whole plot at a glance even with trees still standing on it and the see-through blueprint over the top. The markers clear once it’s built.',
    ],
  },
  {
    date: '2026-07-17',
    title: 'The building panel got a glow-up',
    points: [
      'Redesigned the building panel: the icon now sits in a little crest, health is a slim bar, and — the big one — a producer’s output is a headline card up top (“🌾 4/min → 8 at full crew”) instead of a line of small italic text buried under the workers.',
      'Crew members read cleaner too: each is a pill with a colour-coded pace badge (green = working at speed, amber = slow bare hands), their name, and a ✕ to release them. And the Pause / Demolish buttons now sit side by side as a proper footer.',
    ],
  },
  {
    date: '2026-07-17',
    title: 'Strong backs haul bigger loads',
    points: [
      'A serf’s carrying capacity now comes from Strength: 5 goods per point. A wiry STR 3 settler shoulders 15 at a time; a STR 10 ox hauls 50 — so brawny folk clear a spill or supply a site in far fewer trips. (It used to grow slowly with hauling practice; raw muscle is the thing now.)',
    ],
  },
  {
    date: '2026-07-17',
    title: 'Blueprints show what they’re waiting on',
    points: [
      'Every building site now floats a badge saying what it needs RIGHT NOW: 🪓 fell the trees on the plot, 🪏 dig the high ground down, ⏫ fill the low ground up, 📦 haul the materials in, 🔨 raise the structure. The icon tracks the real bottleneck, so a stalled site (📦 with no haulers spare) is obvious at a glance.',
    ],
  },
  {
    date: '2026-07-17',
    title: 'Serfs stop flickering near the town hall',
    points: [
      'Fixed the jitter/blink you saw when serfs walked past the town hall (most noticeable while building near it): with folk now crossing the hall’s plaza, the renderer was still shoving them off it like a wall — so a serf passing the plaza’s middle snapped around the building each frame. The plaza is open ground now; only the great hall itself keeps its bulk.',
      'Hop boundaries got a touch smoother too — the small position correction when a serf steps to the next hex now glides instead of ticking.',
    ],
  },
  {
    date: '2026-07-17',
    title: 'Crafters carry the material to the bench',
    points: [
      'You can now watch the whole errand: a serf taking a hall order walks to the storage annex, shoulders a bundle of the material, and carries it — visibly, on their back — round to the workbench before setting to work. No more materials appearing from nowhere.',
      'And they walk AROUND the hall, not through it: the great hall’s doorway no longer sits on anyone’s shortcut, so the plaza traffic flows around the building like it should.',
    ],
  },
  {
    date: '2026-07-16',
    title: 'The crafter works at the bench, and the tour is back',
    points: [
      'Fixed the hall crafter’s awkward entrance: a serf answering a craft order used to walk INTO the great hall, blink out and back, and only then start work. They now walk up to the workbench on the east flank and get hired the moment they arrive — no walking through walls, no teleport.',
      'And the trip tells the story: on the way to the bench the crafter swings by the STORAGE ANNEX first — collect the materials, carry them round the hall, set to work.',
      'Fixed the tutorial no longer showing: restarting (or founding) a village mid-session left the tour switched off from the previous village. Every new village now gets its fresh guided start, reload or not.',
    ],
  },
  {
    date: '2026-07-16',
    title: 'Know what you’re crafting',
    points: [
      'Hover any item in a crafting menu and a card tells you what it’s actually FOR — what a serf does with a hammer, why the village wants an axe, what a spear signs them up for — along with its cost, crafting time and weapon stats.',
    ],
  },
  {
    date: '2026-07-16',
    title: 'Nobody walks through the great hall anymore',
    points: [
      'Serfs fetching a tool — sent by you or on their own initiative — now walk to the weapon rack on the town hall’s south wall and make the swap standing right at it, instead of strolling into the middle of the building.',
      'Hungry serfs heading home to eat make for the storage annex on the west flank, same as the haulers — the great hall’s floor is for the crafter, not a canteen.',
      'And the annex is now the hall’s ONLY counter: goods change hands solely on its hex. A hauler reaching any other side of the hall no longer banks their load over a wall — they round the building to the lean-to like everyone else.',
    ],
  },
  {
    date: '2026-07-16',
    title: 'Fresh gear takes its place on the rack',
    points: [
      'Finished pieces no longer teleport onto the hall’s weapon rack: the moment a tool or weapon comes off the bench it lifts from the workbench, sails around the front of the hall — held up big enough to see what was just made — and settles onto its slot between the rails, shrinking to hanging size as it lands.',
    ],
  },
  {
    date: '2026-07-15',
    title: 'Restarting lands you at your new village',
    points: [
      'Fixed restarting a village leaving you staring at empty fog: the camera centred on your new home and was then instantly yanked back toward the OLD village by the map’s pan limits (which still remembered the previous realm). The limits now reset with the world — you arrive looking at your fresh town hall, tutorial and all.',
    ],
  },
  {
    date: '2026-07-15',
    title: 'The hall got its workbench',
    points: [
      'The Town Hall now shows the workshop it always was: a carved workbench stands at its east side — planks stacked, mallet resting, chopping stump beside — and the serf whittling your orders walks up and works right at it, facing the bench, instead of loitering somewhere on the plaza.',
      'Crafters actually LOOK busy now: whoever works the hall bench, the Workbench or the Smithy bends over the work with steady strokes while an order is on the queue — and stands easy when there’s nothing to make.',
      'And the hall shows its other job too: a storage lean-to stands on the west flank, and the crates, sacks and barrels under it GROW with what your hub actually holds — glance at the annex and you know how full the stores are.',
      'The annex is a real depot, not decoration: haulers now walk to IT to pick up and drop off goods, instead of trooping through the great hall’s front door.',
      'And a weapon rack hangs on the hall’s south wall showing your armory’s actual spare gear — every tool or weapon sitting unclaimed on the rack appears mounted between the rails, and comes down when a serf takes it. Other kingdoms see bare rails; your arsenal is your business.',
    ],
  },
  {
    date: '2026-07-15',
    title: 'A hand for new founders',
    points: [
      'New villages get a short guided start: the land dims and a golden arrow spotlights your Town Hall, then the tour walks you through ordering your first Hammer — the two clicks that unlock everything else. It advances as you actually do the steps, glows the button you need, and a ✕ skips it.',
      'The tour runs once per village — found or restart a village and it starts over; finish or skip it and that village never asks again.',
    ],
  },
  {
    date: '2026-07-15',
    title: 'No more shoving matches',
    points: [
      'Serfs now see the crowd ahead: a hex already holding other folk costs more to walk through and a FULL one is worth a real detour, so walkers fan out over parallel lanes instead of queueing single-file and pushing each other around.',
      'A serf stuck behind a jam no longer just leans on it — after a moment they slip around it through a free lane and carry on.',
      'A hex now holds at most FIVE serfs (down from seven) — crowds read as people, not piles. Hexes already holding more simply drain as folk move on.',
    ],
  },
  {
    date: '2026-07-15',
    title: 'Serfs wear their work overhead',
    points: [
      'Every serf now floats a small glyph saying what they’re up to: 🔨 building, 🪓 felling, 🪏 levelling, 🌾 farming, 🎣 fishing, ⛏️ quarrying, 🪚 whittling, ⚒️ smithing, 📦 hauling, 🍞 off to eat, ⚔️ fighting, 🛡️ standing guard — and a faint 💤 over anyone with nothing to do, so slackers can’t hide.',
      'The glyphs show in both views and stay out of the way — hovering a serf swaps the glyph for their name tag as before.',
    ],
  },
  {
    date: '2026-07-15',
    title: 'Your world is safer on disk',
    points: [
      'Hardened the world save: the server now refuses to boot a fresh empty world when a real save exists but can’t be read (previously that could quietly overwrite your world), and every save keeps one previous generation as a backup.',
    ],
  },
  {
    date: '2026-07-14',
    title: 'The wilds got lighter (a big performance fix)',
    points: [
      'Found why the world had been slowing down: every visitor who ever founded a village left behind NPC hamlets and wildlife that lived — and cost simulation time — forever. The server had accumulated over 700 ghost settlements and nearly twenty thousand creatures for a handful of real kingdoms.',
      'The map now keeps NPC villages and wildlife only near living players. Distant hamlets dissolve back into wilderness, far herds thin out, and the game simulates what actually matters. In testing this more than halved the server’s work per second — everything ticks snappier.',
      'Nothing near your borders changes: your NPC neighbours, nearby wolves and deer all stay exactly where they were.',
    ],
  },
  {
    date: '2026-07-14',
    title: 'Empty hands carry',
    points: [
      'A serf with nothing in hand is a hauler, plain and simple. Bare hands no longer pitch in on site clearing, levelling or any other work — they fetch tools from the armory, carry materials to sites, and cart goods between stores. Any tool in hand (even the wrong one) still lets a serf help a site slowly.',
      'One exception: whittling at the Town Hall or Workbench still takes any free serf — it is how a village with no tools makes its first one.',
      'Watch your granary staffing: the crew panel shows who is actually working, and a bare-handed serf pinned to a trade by you still obeys — that choice stays yours.',
    ],
  },
  {
    date: '2026-07-14',
    title: 'Crafting pays as it goes',
    points: [
      'Ordering a tool no longer swallows the whole cost the moment you click. The materials drain from your stores tick by tick while the crafter actually works, landing the full price exactly over the craft’s duration.',
      'If the stores run dry mid-craft, the order simply pauses — the crafter waits, the panel says what’s missing, and work resumes on its own when materials come in. You can queue orders you can’t afford yet; they’ll wait their turn for the wood to arrive.',
    ],
  },
  {
    date: '2026-07-14',
    title: 'Smooth through the lag',
    points: [
      'Lag spikes no longer freeze and teleport your serfs. Your folk now remember the road they were walking: when the connection hiccups they keep going along their route, and when the server catches up they glide to the true position instead of snapping across the map.',
      'A connection that falls behind now skips the stale play-by-play and receives one clean catch-up — no more fast-forward blur when a spike clears. A small “⏳ catching up…” note appears when the network stalls, so a hiccup reads as what it is.',
      'Under the hood: the server now reports its own tick and save timings, and your client measures its connection — so future lag reports can be answered with data instead of guesses.',
    ],
  },
  {
    date: '2026-07-14',
    title: 'Wooden spears; the bow is resting',
    points: [
      'The spear joins the wooden tier: a fire-hardened wooden point (no more steel tip), whittled at the Town Hall alongside the tools. Every village can now arm a watch without a forge.',
      'The bow is out of the game for now — no longer crafted or carried. Any bows in hands, armories, queues or on the ground have been retired; their bearers stand down to bare hands. It will return in better shape.',
      'New villages found with five wooden pieces in hand (hammer, shovel, hoe, fishing rod, spear) and two bare-handed settlers ready to craft and haul.',
    ],
  },
]
